import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  Clipboard,
  Contact,
  Loader2,
  Megaphone,
  Package,
  Percent,
  RefreshCw,
  Search,
  Send,
  Wallet,
  X,
} from 'lucide-react';
import axiosInstance from '../../axiosConfig';
import FeatureGate from '../../components/FeatureGate';
import Shell from '../dashboard/Shell';
import WhatsAppIcon from '../sales-tools/WhatsAppIcon';
import { useToast } from '../../components/ui/Toast';
import './LaunchBoost.css';

const FALLBACK_MAX_CONTACTS = 50;
const SAMPLE = 'Ada, 08031234567\nChinedu, +234 803 123 4567\nMariam, 2348055555555';
const ACTIVE_BROADCAST_STATUSES = new Set(['READY', 'QUEUED', 'SENDING', 'PARTIAL']);
const CAMPAIGN_OPTIONS = [
  { value: 'STORE_LAUNCH', label: 'Launch', icon: Megaphone },
  { value: 'NEW_PRODUCT', label: 'Product', icon: Package },
  { value: 'DISCOUNT_CODE', label: 'Discount', icon: Percent },
  { value: 'PROMO', label: 'Promo', icon: Megaphone },
];
const VARIABLE_REQUIREMENTS = {
  STORE_LAUNCH: [],
  NEW_PRODUCT: ['productName', 'productUrl'],
  DISCOUNT_CODE: ['discountCode', 'discountValue'],
  PROMO: ['promoTitle', 'promoDetails'],
};

function parseContactLine(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed) return null;

  const phoneMatch = trimmed.match(/\+?\d[\d\s().-]{7,}\d/);
  if (!phoneMatch) return null;

  const phone = phoneMatch[0].trim();
  const name = trimmed
    .replace(phoneMatch[0], '')
    .replace(/^[,\s-]+|[,\s-]+$/g, '')
    .trim();

  return {
    name,
    phone,
  };
}

function parseContacts(raw, maxContacts) {
  const seen = new Set();
  return String(raw || '')
    .split(/\n+/)
    .map(parseContactLine)
    .filter(Boolean)
    .filter((contact) => {
      const key = contact.phone.replace(/\D/g, '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, maxContacts);
}

function resultLabel(result) {
  if (result.status === 'sent') return 'Sent';
  if (result.status === 'skipped') return 'Skipped';
  if (result.status === 'retrying') return 'Retry scheduled';
  return 'Failed';
}

function formatLimit(value) {
  if (typeof value === 'undefined') return '...';
  if (value === null || value === -1) return 'Unlimited';
  return Number(value || 0).toLocaleString();
}

function titleCaseSlug(value) {
  return String(value || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function storeNameFromUrl(url) {
  try {
    const hostname = new URL(url).hostname;
    const [subdomain] = hostname.split('.');
    return titleCaseSlug(subdomain);
  } catch {
    return '';
  }
}

function formatBroadcastDate(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('en-NG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

export default function LaunchBoost() {
  const toast = useToast();
  const [rawContacts, setRawContacts] = useState('');
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [config, setConfig] = useState(null);
  const [broadcastConfig, setBroadcastConfig] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [estimateError, setEstimateError] = useState('');
  const [estimateLoading, setEstimateLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetailLoading, setCampaignDetailLoading] = useState(false);
  const [campaignType, setCampaignType] = useState('STORE_LAUNCH');
  const [campaignVariables, setCampaignVariables] = useState({});
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productsLoading, setProductsLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  const useBroadcastEngine = broadcastConfig?.merchantVisible === true;
  const allowedCampaignTypes = useMemo(() => {
    const fromConfig = Array.isArray(broadcastConfig?.campaignTypes) && broadcastConfig.campaignTypes.length
      ? broadcastConfig.campaignTypes
      : ['STORE_LAUNCH'];
    return CAMPAIGN_OPTIONS
      .filter((option) => fromConfig.includes(option.value))
      .map((option) => option.value);
  }, [broadcastConfig]);
  const showCampaignComposer = useBroadcastEngine && allowedCampaignTypes.length > 1;
  const maxContacts = (useBroadcastEngine ? broadcastConfig?.perSend : config?.limits?.perSend) || FALLBACK_MAX_CONTACTS;
  const contacts = useMemo(() => parseContacts(rawContacts, maxContacts), [rawContacts, maxContacts]);
  const firstContact = contacts[0];
  const firstContactName = firstContact?.name || 'there';
  const storeName = response?.store?.name || config?.store?.name || 'your store';
  const storeUrl = response?.store?.url || config?.store?.url || '';
  const previewStoreName = response?.store?.name
    || config?.store?.name
    || config?.store?.businessName
    || config?.storeName
    || config?.businessName
    || storeNameFromUrl(storeUrl)
    || storeName;
  const monthlyRemaining = useBroadcastEngine
    ? estimate?.usage?.remainingThisMonth ?? broadcastConfig?.remainingThisMonth
    : response?.limits?.remainingThisMonth ?? config?.limits?.remainingThisMonth;
  const monthlyLimit = useBroadcastEngine
    ? broadcastConfig?.monthly
    : response?.limits?.monthly ?? config?.limits?.monthly;
  const readyContactsCount = useBroadcastEngine
    ? estimate?.contacts?.valid ?? contacts.length
    : contacts.length;
  const suppressedContactsCount = useBroadcastEngine
    ? estimate?.contacts?.suppressed || 0
    : 0;
  const campaignPayloadVariables = useMemo(() => ({
    storeName: previewStoreName,
    storeUrl,
    ...campaignVariables,
  }), [campaignVariables, previewStoreName, storeUrl]);
  const requiredVariableNames = VARIABLE_REQUIREMENTS[campaignType] || [];
  const requiredVariablesReady = requiredVariableNames.every((key) => String(campaignPayloadVariables[key] || '').trim());
  const previewMessage = useMemo(() => {
    const name = firstContactName;
    if (campaignType === 'NEW_PRODUCT') {
      return (
        <>
          <p>Hi {name}, <strong>{previewStoreName}</strong> just added <strong>{campaignPayloadVariables.productName || 'a new product'}</strong>.</p>
          <p>
            See it here:<br />
            {campaignPayloadVariables.productUrl || 'https://yourstore.userevstar.com/products/new'}
          </p>
          <p>Reply here if you want help choosing or ordering.</p>
        </>
      );
    }
    if (campaignType === 'DISCOUNT_CODE') {
      return (
        <>
          <p>Hi {name}, <strong>{previewStoreName}</strong> has {campaignPayloadVariables.discountValue || 'a discount'} off for you.</p>
          <p>Use code <strong>{campaignPayloadVariables.discountCode || 'REVSTAR'}</strong>.</p>
          <p>
            Shop here:<br />
            {storeUrl || 'https://yourstore.userevstar.com'}
          </p>
        </>
      );
    }
    if (campaignType === 'PROMO') {
      return (
        <>
          <p>Hi {name}, <strong>{previewStoreName}</strong> has a promo: <strong>{campaignPayloadVariables.promoTitle || 'Limited offer'}</strong>.</p>
          <p>{campaignPayloadVariables.promoDetails || 'Grab it while it is live.'}</p>
          <p>
            Shop here:<br />
            {storeUrl || 'https://yourstore.userevstar.com'}
          </p>
        </>
      );
    }
    return (
      <>
        <p>
          Hi {name}, <strong>{previewStoreName}</strong> just launched their online store on Revstar.
        </p>
        <p>
          Shop here:<br />
          {storeUrl || 'https://yourstore.userevstar.com'}
        </p>
        <p>Reply here if you need help choosing or ordering.</p>
      </>
    );
  }, [campaignPayloadVariables, campaignType, firstContactName, previewStoreName, storeUrl]);
  const campaignActionLabel = campaignType === 'STORE_LAUNCH'
    ? 'Launch Boost'
    : CAMPAIGN_OPTIONS.find((option) => option.value === campaignType)?.label || 'Broadcast';
  const walletSufficient = !useBroadcastEngine || estimate?.wallet?.sufficient !== false;
  const canSend = !!config
    && contacts.length > 0
    && contacts.length <= maxContacts
    && consent
    && !sending
    && !estimateLoading
    && (!useBroadcastEngine || !!estimate)
    && (!useBroadcastEngine || readyContactsCount > 0)
    && (!useBroadcastEngine || requiredVariablesReady)
    && walletSufficient
    && !estimateError
    && config.enabled !== false;

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const [legacyRes, broadcastRes] = await Promise.all([
        axiosInstance.get('/whatsapp/v2/messages/launch-boost/config'),
        axiosInstance.get('/whatsapp/v2/broadcast/config').catch(() => null),
      ]);
      setConfig(legacyRes.data);
      setBroadcastConfig(broadcastRes?.data?.data || null);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Could not load Launch Boost settings.';
      toast.error(message);
    } finally {
      setConfigLoading(false);
    }
  };

  const loadCampaigns = async ({ silent = false } = {}) => {
    if (!useBroadcastEngine) {
      setCampaigns([]);
      return;
    }

    if (!silent) setCampaignsLoading(true);
    try {
      const { data } = await axiosInstance.get('/whatsapp/v2/broadcast/campaigns?limit=6');
      setCampaigns(data?.data || []);
    } catch {
      setCampaigns([]);
    } finally {
      if (!silent) setCampaignsLoading(false);
    }
  };

  const openCampaignDetail = async (campaign, { silent = false } = {}) => {
    if (!silent) setSelectedCampaign({ ...campaign, recipients: [] });
    if (!silent) setCampaignDetailLoading(true);
    try {
      const { data } = await axiosInstance.get(`/whatsapp/v2/broadcast/campaigns/${campaign.id}`);
      setSelectedCampaign(data?.data || campaign);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Could not load campaign detail.');
    } finally {
      if (!silent) setCampaignDetailLoading(false);
    }
  };

  const updateCampaignVariable = (key, value) => {
    setCampaignVariables((current) => ({ ...current, [key]: value }));
    setResponse(null);
  };

  const selectProduct = (productId) => {
    const product = products.find((item) => String(item.id) === String(productId));
    const productName = product?.name || product?.title || '';
    const productSlug = product?.slug || product?.handle || '';
    setCampaignVariables((current) => ({
      ...current,
      productId: product?.id || '',
      productName,
      productUrl: productSlug && storeUrl ? `${storeUrl.replace(/\/$/, '')}/products/${productSlug}` : current.productUrl || '',
    }));
    setResponse(null);
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allowedCampaignTypes.includes(campaignType)) return;
    setCampaignType(allowedCampaignTypes[0] || 'STORE_LAUNCH');
    setCampaignVariables({});
    setResponse(null);
  }, [allowedCampaignTypes, campaignType]);

  useEffect(() => {
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBroadcastEngine]);

  useEffect(() => {
    if (!useBroadcastEngine) return undefined;
    const timer = window.setInterval(() => {
      loadCampaigns({ silent: true });
    }, 15000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBroadcastEngine]);

  useEffect(() => {
    if (!useBroadcastEngine || !selectedCampaign?.id || !ACTIVE_BROADCAST_STATUSES.has(selectedCampaign.status)) return undefined;
    const timer = window.setInterval(() => {
      openCampaignDetail(selectedCampaign, { silent: true });
    }, 12000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBroadcastEngine, selectedCampaign?.id, selectedCampaign?.status]);

  useEffect(() => {
    if (!showCampaignComposer || campaignType !== 'NEW_PRODUCT') return undefined;
    let active = true;
    const timer = window.setTimeout(async () => {
      setProductsLoading(true);
      try {
        const { data } = await axiosInstance.get('/merchants/products', {
          params: {
            limit: 20,
            status: 'ACTIVE',
            search: productSearch || undefined,
          },
        });
        if (active) setProducts(data?.products || data?.data || []);
      } catch {
        if (active) setProducts([]);
      } finally {
        if (active) setProductsLoading(false);
      }
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [campaignType, productSearch, showCampaignComposer]);

  useEffect(() => {
    if (!previewOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setPreviewOpen(false);
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [previewOpen]);

  useEffect(() => {
    if (!useBroadcastEngine || contacts.length === 0 || !requiredVariablesReady) {
      setEstimate(null);
      setEstimateError('');
      setEstimateLoading(false);
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setEstimateLoading(true);
      setEstimateError('');
      try {
        const { data } = await axiosInstance.post('/whatsapp/v2/broadcast/estimate', {
          type: campaignType,
          contacts,
          variables: campaignPayloadVariables,
          senderMode: 'AUTO',
        });
        if (!active) return;
        setEstimate(data?.data || null);
      } catch (err) {
        if (!active) return;
        setEstimate(null);
        setEstimateError(err.response?.data?.error || err.message || 'Could not estimate broadcast cost.');
      } finally {
        if (active) setEstimateLoading(false);
      }
    }, 420);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [campaignPayloadVariables, campaignType, contacts, requiredVariablesReady, useBroadcastEngine]);

  const handlePasteSample = () => {
    setRawContacts(SAMPLE);
    setResponse(null);
  };

  const handleImportContacts = async () => {
    const contactsApi = navigator.contacts;
    if (!contactsApi?.select) {
      toast.info('Contact import works only on supported mobile browsers. Paste contacts instead.');
      return;
    }

    try {
      const picked = await contactsApi.select(['name', 'tel'], { multiple: true });
      const lines = picked
        .map((contact) => {
          const name = Array.isArray(contact.name) ? contact.name[0] : contact.name;
          const phone = Array.isArray(contact.tel) ? contact.tel[0] : contact.tel;
          if (!phone) return null;
          return [name, phone].filter(Boolean).join(', ');
        })
        .filter(Boolean);

      if (lines.length === 0) {
        toast.info('No phone numbers were selected.');
        return;
      }

      setRawContacts((current) => [current.trim(), lines.join('\n')].filter(Boolean).join('\n'));
      setResponse(null);
      toast.success(`Imported ${lines.length} contact${lines.length === 1 ? '' : 's'}.`);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error('Could not import contacts. Paste contacts instead.');
      }
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setResponse(null);
    try {
      let normalizedResponse;
      if (useBroadcastEngine) {
        const campaignRes = await axiosInstance.post('/whatsapp/v2/broadcast/campaigns', {
          type: campaignType,
          contacts,
          variables: campaignPayloadVariables,
          consentConfirmed: consent,
          senderMode: 'AUTO',
        });
        const campaignId = campaignRes.data?.data?.id;
        const sendRes = await axiosInstance.post(`/whatsapp/v2/broadcast/campaigns/${campaignId}/send`);
        const payload = sendRes.data?.data || {};
        normalizedResponse = {
          ok: payload.campaign?.status !== 'FAILED',
          queued: payload.queued === true,
          store: estimate?.store || config?.store || null,
          summary: payload.summary || payload.campaign?.summary || {},
          results: payload.results || [],
          campaign: payload.campaign,
        };
      } else {
        const { data } = await axiosInstance.post('/whatsapp/v2/messages/share-store', {
          contacts,
          consentConfirmed: consent,
        });
        normalizedResponse = data;
      }

      setResponse(normalizedResponse);
      const sent = normalizedResponse?.summary?.sent || 0;
      const skipped = normalizedResponse?.summary?.skipped || 0;
      const queued = normalizedResponse?.summary?.queued || (normalizedResponse?.queued ? normalizedResponse?.campaign?.contactCount || 0 : 0);
      if (queued > 0) {
        toast.success(`${campaignActionLabel} queued for ${queued} contact${queued === 1 ? '' : 's'}.`);
      } else if (sent > 0) {
        toast.success(`${campaignActionLabel} sent to ${sent} contact${sent === 1 ? '' : 's'}.`);
      } else if (skipped > 0) {
        toast.info(useBroadcastEngine
          ? `${skipped} contact${skipped === 1 ? '' : 's'} skipped.`
          : 'Those contacts already received this launch message.');
      } else {
        toast.warning('No launch messages were sent.');
      }
      loadConfig();
      loadCampaigns();
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Launch Boost failed.';
      setResponse({
        ok: false,
        summary: { sent: 0, failed: contacts.length, skipped: 0 },
        results: [],
        error: message,
      });
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const renderPhonePreview = () => (
    <div className="wlb-phone">
      <div className="wlb-phone__bar" />
      <div className="wlb-phone__header">
        <span className="wlb-phone__avatar">
          <WhatsAppIcon size={21} className="wlb-phone__avatar-icon" />
        </span>
        <div>
          <strong>Revstar</strong>
          <span>WhatsApp</span>
        </div>
      </div>
      <div className="wlb-phone__chat">
        <div className="wlb-template">
          <div className="wlb-template__media">
            <WhatsAppIcon size={32} />
          </div>
          <div className="wlb-template__body">
            {previewMessage}
            <p>Reply STOP to opt out.</p>
          </div>
          <time>Now</time>
        </div>

        {response && (
          <div className={`wlb-status-card${response.ok ? ' is-ok' : ' is-error'}`}>
            <div className="wlb-status-card__head">
              {response.ok ? <CheckCircle2 size={17} /> : <AlertCircle size={17} />}
              <strong>{response.ok ? 'Sent' : 'Issue'}</strong>
            </div>
            {response.error && <p>{response.error}</p>}
            <div className="wlb-mini-stats">
              {response.queued && <span><strong>{response.summary?.queued || response.campaign?.contactCount || 0}</strong> Queued</span>}
              <span><strong>{response.summary?.sent || 0}</strong> Sent</span>
              <span><strong>{response.summary?.skipped || 0}</strong> Skipped</span>
              <span><strong>{response.summary?.failed || 0}</strong> Failed</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Shell>
      <FeatureGate
        featureId="marketing.launch_boost"
        upgradeMessage="Launch Boost is not available on your current plan."
      >
        <main className="wlb-page">
          <div className="wlb-page__inner">
            <section className="wlb-top">
              <div>
                <p className="wlb-kicker">Launch Boost</p>
                <h1>Send your store launch on WhatsApp</h1>
                <p className="wlb-subtitle">Paste opted-in contacts. Revstar sends the approved template.</p>
              </div>

              <div className="wlb-top__actions">
                {config?.assistant?.waLink && (
                  <a
                    className="wlb-assistant"
                    href={config.assistant.waLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WhatsAppIcon size={17} />
                    <span>Ask Revstar</span>
                  </a>
                )}
                <button type="button" className="wlb-icon-button" onClick={loadConfig} disabled={configLoading}>
                  <RefreshCw size={17} className={configLoading ? 'wlb-spin' : ''} />
                  <span>Refresh</span>
                </button>
              </div>
            </section>

            <section className="wlb-stats" aria-label="Launch Boost limits">
              <div>
                <span>Per send</span>
                <strong>{formatLimit(maxContacts)}</strong>
              </div>
              <div>
                <span>This month</span>
                <strong>{formatLimit(monthlyRemaining)}</strong>
              </div>
              <div>
                <span>Plan</span>
                <strong>{config?.tier || '...'}</strong>
              </div>
            </section>

            <section className="wlb-workspace">
              <div className="wlb-panel wlb-compose">
                {showCampaignComposer && (
                  <section className="wlb-campaign-config" aria-label="Broadcast campaign setup">
                    <div className="wlb-segmented" role="tablist" aria-label="Broadcast type">
                      {CAMPAIGN_OPTIONS.filter((option) => allowedCampaignTypes.includes(option.value)).map((option) => {
                        const Icon = option.icon;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={campaignType === option.value ? 'is-active' : ''}
                            onClick={() => {
                              setCampaignType(option.value);
                              setCampaignVariables({});
                              setResponse(null);
                            }}
                          >
                            <Icon size={15} />
                            <span>{option.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {campaignType === 'NEW_PRODUCT' && (
                      <div className="wlb-product-picker">
                        <label>
                          <span>Pick product</span>
                          <div>
                            <Search size={15} />
                            <input
                              value={productSearch}
                              onChange={(event) => setProductSearch(event.target.value)}
                              placeholder="Search active products"
                            />
                          </div>
                        </label>
                        <select
                          value={campaignVariables.productId || ''}
                          onChange={(event) => selectProduct(event.target.value)}
                          disabled={productsLoading}
                        >
                          <option value="">{productsLoading ? 'Loading products' : 'Select a product'}</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name || product.title || 'Untitled product'}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {campaignType === 'NEW_PRODUCT' && (
                      <div className="wlb-variable-grid">
                        <label>
                          <span>Product name</span>
                          <input value={campaignVariables.productName || ''} onChange={(event) => updateCampaignVariable('productName', event.target.value)} placeholder="Skinny jeans" />
                        </label>
                        <label>
                          <span>Product link</span>
                          <input value={campaignVariables.productUrl || ''} onChange={(event) => updateCampaignVariable('productUrl', event.target.value)} placeholder="https://store/products/item" />
                        </label>
                      </div>
                    )}

                    {campaignType === 'DISCOUNT_CODE' && (
                      <div className="wlb-variable-grid">
                        <label>
                          <span>Discount code</span>
                          <input value={campaignVariables.discountCode || ''} onChange={(event) => updateCampaignVariable('discountCode', event.target.value.toUpperCase())} placeholder="REVSTAR20" />
                        </label>
                        <label>
                          <span>Discount value</span>
                          <input value={campaignVariables.discountValue || ''} onChange={(event) => updateCampaignVariable('discountValue', event.target.value)} placeholder="20%" />
                        </label>
                      </div>
                    )}

                    {campaignType === 'PROMO' && (
                      <div className="wlb-variable-grid">
                        <label>
                          <span>Promo title</span>
                          <input value={campaignVariables.promoTitle || ''} onChange={(event) => updateCampaignVariable('promoTitle', event.target.value)} placeholder="Weekend drop" />
                        </label>
                        <label>
                          <span>Promo details</span>
                          <input value={campaignVariables.promoDetails || ''} onChange={(event) => updateCampaignVariable('promoDetails', event.target.value)} placeholder="Free delivery until Sunday" />
                        </label>
                      </div>
                    )}
                  </section>
                )}

                <header className="wlb-panel__head">
                  <div>
                    <h2>Contacts</h2>
                    <p>{readyContactsCount} ready{suppressedContactsCount ? `, ${suppressedContactsCount} opted out` : ''}</p>
                  </div>
                  <div className="wlb-actions">
                    <button type="button" className="wlb-tool" onClick={handleImportContacts} aria-label="Import contacts">
                      <Contact size={17} />
                      <span>Import</span>
                    </button>
                    <button type="button" className="wlb-tool" onClick={handlePasteSample} aria-label="Paste example">
                      <Clipboard size={17} />
                      <span>Example</span>
                    </button>
                    <button
                      type="button"
                      className="wlb-tool wlb-mobile-preview"
                      onClick={() => setPreviewOpen(true)}
                      aria-label="Preview WhatsApp message"
                    >
                      <WhatsAppIcon size={17} />
                      <span>Preview</span>
                    </button>
                  </div>
                </header>

                <textarea
                  className="wlb-textarea"
                  value={rawContacts}
                  onChange={(event) => {
                    setRawContacts(event.target.value);
                    setResponse(null);
                  }}
                  rows={12}
                  placeholder="Ada, 08031234567"
                />

                <div className="wlb-counter">
                  <span>{readyContactsCount} ready / {maxContacts}</span>
                  <span>{monthlyLimit === -1 ? 'No monthly cap' : `${formatLimit(monthlyRemaining)} left this month`}</span>
                </div>

                {useBroadcastEngine && (
                  <div className={`wlb-cost${walletSufficient ? '' : ' is-low'}`}>
                    <div>
                      <Wallet size={17} />
                      <span>{estimateLoading ? 'Checking wallet' : 'Wallet estimate'}</span>
                    </div>
                    <strong>
                      {estimateLoading
                        ? '...'
                        : `${estimate?.pricing?.currency || broadcastConfig?.currency || 'NGN'} ${Number(estimate?.pricing?.estimatedCost || 0).toLocaleString()}`}
                    </strong>
                    {estimateError ? (
                      <p>{estimateError}</p>
                    ) : (
                      <p>
                        {walletSufficient
                          ? `${Number(estimate?.wallet?.availableBalance || 0).toLocaleString()} available`
                          : `Wallet needs ${Number(estimate?.wallet?.required || 0).toLocaleString()}`}
                      </p>
                    )}
                  </div>
                )}

                <label className="wlb-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                  />
                  <span>Contacts gave WhatsApp consent.</span>
                </label>

                <button type="button" className="wlb-send" disabled={!canSend} onClick={handleSend}>
                  {sending ? <Loader2 size={18} className="wlb-spin" /> : <Send size={18} />}
                  {sending ? 'Sending' : `Send ${campaignActionLabel}`}
                </button>
              </div>

              <aside className="wlb-phone-panel" aria-label="WhatsApp message preview">
                {renderPhonePreview()}
              </aside>
            </section>

            {previewOpen && (
              <div
                className="wlb-preview-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="WhatsApp message preview"
                onClick={() => setPreviewOpen(false)}
              >
                <div className="wlb-preview-modal" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    className="wlb-preview-close"
                    onClick={() => setPreviewOpen(false)}
                    aria-label="Close preview"
                  >
                    <X size={18} />
                  </button>
                  {renderPhonePreview()}
                </div>
              </div>
            )}

            {response && (response.queued || (Array.isArray(response.results) && response.results.length > 0)) && (
              <section className={`wlb-results${response.ok ? ' is-ok' : ' is-error'}`}>
                <header>
                  <div>
                    <h2>{response.queued ? `${campaignActionLabel} queued` : response.ok ? `${campaignActionLabel} complete` : `${campaignActionLabel} issue`}</h2>
                    <p>
                      {response.queued
                        ? `${response.summary?.queued || response.campaign?.contactCount || 0} queued for background sending.`
                        : `${response.summary?.sent || 0} sent, ${response.summary?.skipped || 0} skipped, ${response.summary?.failed || 0} failed.`}
                    </p>
                  </div>
                  {config?.assistant?.waLink && (
                    <a href={config.assistant.waLink} target="_blank" rel="noreferrer">
                      <WhatsAppIcon size={16} />
                      <span>Chat with Revstar</span>
                    </a>
                  )}
                </header>
                {Array.isArray(response.results) && response.results.length > 0 && (
                  <div className="wlb-result-list">
                    {response.results.slice(0, 12).map((result) => (
                      <div key={`${result.phone}-${result.status}`} className={`wlb-result-row is-${result.status}`}>
                        <span>{result.phone}</span>
                        <strong>{resultLabel(result)}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {useBroadcastEngine && (
              <section className="wlb-history">
                <header>
                  <div>
                    <h2>Recent broadcasts</h2>
                    <p>{campaignsLoading ? 'Loading campaign history' : `${campaigns.length} latest campaign${campaigns.length === 1 ? '' : 's'}`}</p>
                  </div>
                  <button type="button" className="wlb-history__refresh" onClick={loadCampaigns} disabled={campaignsLoading}>
                    <RefreshCw size={15} className={campaignsLoading ? 'wlb-spin' : ''} />
                  </button>
                </header>

                {campaigns.length === 0 && !campaignsLoading ? (
                  <div className="wlb-history__empty">No broadcast campaigns yet.</div>
                ) : (
                  <div className="wlb-history__list">
                    {campaigns.map((campaign) => (
                      <button type="button" className="wlb-history__row" key={campaign.id} onClick={() => openCampaignDetail(campaign)}>
                        <div>
                          <strong>{campaign.name}</strong>
                          <span><Clock3 size={13} /> {formatBroadcastDate(campaign.createdAt)}</span>
                        </div>
                        <div className="wlb-history__metrics">
                          <span>{campaign.status}</span>
                          <span>{campaign.sentCount || 0}/{campaign.contactCount || 0} sent</span>
                          <span>{campaign.currency || 'NGN'} {Number(campaign.finalCost || campaign.estimatedCost || 0).toLocaleString()}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {selectedCampaign && (
              <div className="wlb-campaign-overlay" role="dialog" aria-modal="true" aria-label="Broadcast campaign detail" onClick={() => setSelectedCampaign(null)}>
                <aside className="wlb-campaign-drawer" onClick={(event) => event.stopPropagation()}>
                  <header>
                    <div>
                      <span>{selectedCampaign.type}</span>
                      <h2>{selectedCampaign.name}</h2>
                      <p>{formatBroadcastDate(selectedCampaign.createdAt)}</p>
                    </div>
                    <button type="button" onClick={() => setSelectedCampaign(null)} aria-label="Close campaign detail">
                      <X size={18} />
                    </button>
                  </header>

                  <div className="wlb-campaign-stats">
                    <div><span>Status</span><strong>{selectedCampaign.status}</strong></div>
                    <div><span>Contacts</span><strong>{selectedCampaign.contactCount || 0}</strong></div>
                    <div><span>Sent</span><strong>{selectedCampaign.sentCount || 0}</strong></div>
                    <div><span>Cost</span><strong>{selectedCampaign.currency || 'NGN'} {Number(selectedCampaign.finalCost || selectedCampaign.estimatedCost || 0).toLocaleString()}</strong></div>
                  </div>

                  <section className="wlb-recipient-panel">
                    <h3>Recipients</h3>
                    {campaignDetailLoading ? (
                      <div className="wlb-history__empty">Loading recipients...</div>
                    ) : (
                      <div className="wlb-recipient-list">
                        {(selectedCampaign.recipients || []).map((recipient) => (
                          <div key={recipient.id} className="wlb-recipient-row">
                            <div>
                              <strong>{recipient.contactName || recipient.phone}</strong>
                              <span>{recipient.phone}</span>
                            </div>
                            <div>
                              <strong>{recipient.status}</strong>
                              {recipient.errorMessage && <span>{recipient.errorMessage}</span>}
                            </div>
                          </div>
                        ))}
                        {(selectedCampaign.recipients || []).length === 0 && (
                          <div className="wlb-history__empty">No recipients found.</div>
                        )}
                      </div>
                    )}
                  </section>
                </aside>
              </div>
            )}
          </div>
        </main>
      </FeatureGate>
    </Shell>
  );
}
